import {
	faCircleExclamation,
	faUser,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { PortalAccountScope } from '@supply-mover-2-0/shared';
import { useTranslation } from '@supply-mover-2-0/web-ui';
import { useQuery } from '@tanstack/react-query';
import type { JSX } from 'react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthGate } from '../../../hooks/useAuthGate';
import { useDisplaySettings } from '../../../hooks/useDisplaySettings';
import { usePortalAuth } from '../../../hooks/usePortalAuth';
import { getStorefrontAccountSnapshot } from '../../../services/portalDashboardService';
import { SnapshotMetricsSkeleton } from '../../skeletons';

/**
 * Account Snapshot widget — open balance, credit limit, available
 * credit, optional credit-hold flag. Two CTA links route to the
 * Invoices and Account pages where the customer can drill in.
 *
 * Login-required. For anonymous visitors the widget renders a sign-in
 * prompt rather than disappearing — the homepage stays usable for
 * browsing, and the widget doubles as a sign-in conversion surface.
 */
export function AccountSnapshotWidget({
	title,
}: {
	title?: string;
}): JSX.Element | null {
	const { t } = useTranslation();
	const requireAuth = useAuthGate();
	const navigate = useNavigate();
	const display = useDisplaySettings();
	const { token, activeClientId } = usePortalAuth();

	// PD-14239: open balance / credit / account funds are purely financial —
	// don't render for hide-pricing users (server forces canViewInvoices off).
	// Defensive: the dispatch already skips mounting this widget.
	const canViewInvoices = display.canViewInvoices;

	// PD-14188: roll-up scope (current account / parent umbrella / ownership
	// umbrella). Reset to the active account whenever the visitor switches
	// accounts — the new account may belong to different (or no) umbrellas.
	const [scope, setScope] = useState<PortalAccountScope>('current');
	useEffect(() => {
		setScope('current');
	}, [activeClientId]);

	const { data, isLoading, isError } = useQuery({
		// Key includes the active client + scope so the widget refetches on an
		// account switch (PD-14188 — the old key omitted activeClientId and
		// went stale) and on a roll-up change.
		queryKey: [
			'portal',
			'dashboard',
			'account-snapshot',
			activeClientId,
			scope,
		],
		queryFn: () => getStorefrontAccountSnapshot(token, scope),
		enabled: token != null,
		staleTime: 60_000,
	});

	const snapshot = data?.data ?? null;
	const availableScopes = snapshot?.availableScopes ?? [];

	// PD-14256: the dropdown lists the top-level entries (current account +
	// umbrella roll-ups) first, then each individual child / ownership account
	// in its own optgroup — same grouping language as the Location picker.
	const topLevelScopes = availableScopes.filter((opt) => opt.group == null);
	const childScopes = availableScopes.filter((opt) => opt.group === 'child');
	const ownershipScopes = availableScopes.filter(
		(opt) => opt.group === 'ownership',
	);

	// PD-14239: bail after the hooks (rules-of-hooks) so hide-pricing users
	// never see balances, credit, or account funds.
	if (!canViewInvoices) {
		return null;
	}

	return (
		<section className="w-full h-full flex flex-col bg-brand-surface border border-brand-border rounded-xl p-4 md:p-5">
			<header className="flex items-center justify-between gap-2 mb-4">
				<h2 className="flex items-center gap-2 text-base md:text-lg font-semibold text-gray-900 dark:text-gray-100">
					<FontAwesomeIcon icon={faUser} className="text-portal-accent" />
					{t(title ?? 'Account Balance & Credit')}
				</h2>
				{/* PD-14188: roll-up scope picker — only when the active account
				    belongs to a parent/ownership umbrella the visitor can see.
				    PD-14256: also lists each individual child / ownership account
				    so the customer can view any one account's balance & credit. */}
				{availableScopes.length > 1 && (
					<select
						aria-label={t('Roll up account summary')}
						value={scope}
						onChange={(e) => {
							setScope(e.target.value as PortalAccountScope);
						}}
						className="text-xs md:text-sm rounded-md border border-brand-border bg-brand-input text-gray-900 dark:text-gray-100 px-2 py-1 max-w-[55%]"
					>
						{topLevelScopes.map((opt) => (
							<option key={opt.scope} value={opt.scope}>
								{t(opt.label)}
								{opt.accountCount > 1 ? ` (${opt.accountCount})` : ''}
							</option>
						))}
						{childScopes.length > 0 && (
							<optgroup label={t('Child Accounts')}>
								{childScopes.map((opt) => (
									<option key={opt.scope} value={opt.scope}>
										{t(opt.label)}
									</option>
								))}
							</optgroup>
						)}
						{ownershipScopes.length > 0 && (
							<optgroup label={t('Ownership Accounts')}>
								{ownershipScopes.map((opt) => (
									<option key={opt.scope} value={opt.scope}>
										{t(opt.label)}
									</option>
								))}
							</optgroup>
						)}
					</select>
				)}
			</header>

			{token == null ? (
				<div className="flex-1 flex flex-col items-center justify-center text-center gap-3">
					<p className="text-sm text-brand-muted max-w-xs">
						{t('Sign in to see your balance, credit, and recent activity.')}
					</p>
					<button
						type="button"
						onClick={() => requireAuth()}
						className="px-4 py-2 text-sm font-medium rounded-md bg-portal-accent text-white hover:opacity-90"
					>
						{t('Sign in')}
					</button>
				</div>
			) : isLoading ? (
				/* Shaped like the metric grid below so numbers land without a
				   jump (PD-14692). */
				<SnapshotMetricsSkeleton />
			) : isError || snapshot == null ? (
				<EmptyMessage message={t("We couldn't load your account right now.")} />
			) : (
				<>
					<div className="flex-1 grid grid-cols-2 gap-4">
						{/* PD-14239: openBalance is null when the active client hides
						    pricing — guard like the other money metrics (the whole
						    widget is also hidden via canViewInvoices, this keeps the
						    type honest + avoids a crash if it ever renders). */}
						<MetricBlock
							label={t('Open balance')}
							value={
								snapshot.openBalance != null
									? formatCurrency(snapshot.openBalance, t)
									: t('—')
							}
						/>
						<MetricBlock
							label={t('Available credit')}
							value={
								snapshot.availableCredit != null
									? formatCurrency(snapshot.availableCredit, t)
									: t('—')
							}
							sublabel={
								snapshot.creditLimit != null
									? `${t('Limit')}: ${formatCurrency(snapshot.creditLimit, t)}`
									: undefined
							}
						/>
						{/* PD-13704: surface the customer's on-account funds (money
						    paid that isn't applied to an invoice). Shown only when
						    they have a balance, so accounts with none aren't cluttered. */}
						{snapshot.accountFunds != null && snapshot.accountFunds > 0 && (
							<MetricBlock
								label={t('Available funds')}
								value={formatCurrency(snapshot.accountFunds, t)}
							/>
						)}
					</div>

					{snapshot.hasCreditHold && (
						<div className="mt-3 p-2 rounded-md bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700/50 flex items-start gap-2 text-xs text-yellow-800 dark:text-yellow-200">
							<FontAwesomeIcon icon={faCircleExclamation} className="mt-0.5" />
							<span>
								{t(
									'Your account is currently on credit hold. Contact your sales rep for details.',
								)}
							</span>
						</div>
					)}

					{/* PD-14691: the balance's natural next step. Shown whenever the
					    visitor can pay (MakePaymentPage also handles the zero-balance
					    case — paying on account / adding funds), so the action is
					    always one tap from the balance it settles. */}
					{display.canPayInvoices && (
						<button
							type="button"
							onClick={() => {
								void navigate('/payments/make');
							}}
							className="mt-4 w-full px-4 py-2.5 text-sm bg-portal-accent hover:bg-portal-accent-hover text-white font-medium rounded-lg min-h-touch"
						>
							{t('Make a Payment')}
						</button>
					)}
				</>
			)}
		</section>
	);
}

function MetricBlock({
	label,
	value,
	sublabel,
}: {
	label: string;
	value: string;
	sublabel?: string;
}): JSX.Element {
	return (
		<div>
			<p className="text-[11px] uppercase tracking-wide text-brand-muted">
				{label}
			</p>
			<p className="mt-0.5 text-lg md:text-xl font-bold text-gray-900 dark:text-gray-100">
				{value}
			</p>
			{sublabel != null && (
				<p className="text-[11px] text-brand-muted mt-0.5">{sublabel}</p>
			)}
		</div>
	);
}

function EmptyMessage({ message }: { message: string }): JSX.Element {
	return (
		<div className="flex-1 flex items-center justify-center text-center px-4 py-6">
			<p className="text-sm text-brand-muted max-w-md">{message}</p>
		</div>
	);
}

function formatCurrency(amount: number, t: (s: string) => string): string {
	const sign = amount < 0 ? '-' : '';
	const absAmount = Math.abs(amount);
	return `${sign}${t('$')}${absAmount.toLocaleString(undefined, {
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	})}`;
}
